import type { ContentItem, ContentType } from '@kidshub/contracts';
import { contentTypeSchema } from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, ButtonLink, EmptyState, Icon, Segmented } from '../ui/kit.js';
import { CollectionsRail, collectionFelt, LOOSE_ITEM_FELT } from './collections-rail.js';
import { ContentCard } from './content-card.js';
import './library.css';

type TypeFilter = ContentType | 'ALL';

/**
 * The library.
 *
 * Collections first, each with its per-child switch, because a collection that
 * is switched off is the most common reason nothing reaches a tablet. Then the
 * items, every one carrying the same fixed answer to who can see it. The count
 * over the list is the two facts a parent actually wants on arriving: how much
 * is here, and how much of it has been opened to anybody.
 */
export const LibraryPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const content = useResource('content', () => apiClient.catalog.list());
  const children = useResource('children', () => apiClient.children.list());
  const collections = useResource('collections', () => apiClient.collections.list());

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  const [query, setQuery] = useState('');

  const items = content.resource.status === 'ready' ? content.resource.data.items : [];
  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];
  const allCollections =
    collections.resource.status === 'ready' ? collections.resource.data.collections : [];

  const needle = query.trim().toLocaleLowerCase(locale);
  const visible = items
    .filter((item) => typeFilter === 'ALL' || item.contentType === typeFilter)
    .filter((item) => needle === '' || item.title.toLocaleLowerCase(locale).includes(needle));

  // Only the kinds the family actually has: a filter for "Games" in a library
  // of videos is a button that can only ever empty the list.
  const presentTypes = contentTypeSchema.options.filter((contentType) =>
    items.some((item) => item.contentType === contentType),
  );

  const nativeLanguages = familyChildren.map((entry) => entry.profile.nativeLanguage);

  const feltFor = (item: ContentItem) => {
    const home = allCollections.find((collection) => collection.itemIds.includes(item.id));
    return home === undefined ? LOOSE_ITEM_FELT : collectionFelt(home.id);
  };

  const replaceItem = (updated: ContentItem) => {
    content.replace({
      items: items.map((item) => (item.id === updated.id ? updated : item)),
    });
  };

  const dropItem = (removed: ContentItem) => {
    content.replace({ items: items.filter((item) => item.id !== removed.id) });
    // The collection cards count from the item list, and a removed item may
    // have been in one, so the counts have to come back from the server.
    collections.reload();
  };

  const clearFilters = () => {
    setQuery('');
    setTypeFilter('ALL');
  };

  const failure =
    content.resource.status === 'failed'
      ? content.resource.error
      : children.resource.status === 'failed'
        ? children.resource.error
        : collections.resource.status === 'failed'
          ? collections.resource.error
          : null;

  const addChannel = (
    <ButtonLink variant="primary" icon="plus" to="/library/import">
      {t('library.importSource')}
    </ButtonLink>
  );
  const addContent = (
    <ButtonLink variant="secondary" to="/library/add">
      {t('library.addContent')}
    </ButtonLink>
  );

  const emptyState = () => {
    if (items.length === 0) {
      return (
        <EmptyState
          title={t('library.emptyTitle')}
          detail={t('library.emptyDetail')}
          actions={
            <>
              {addChannel}
              {addContent}
            </>
          }
        />
      );
    }
    return (
      <EmptyState
        title={t('library.noResults')}
        detail={t('library.noneMatch')}
        actions={<Button onClick={clearFilters}>{t('library.clearFilters')}</Button>}
      />
    );
  };

  return (
    <AppShell
      title={t('library.title')}
      subtitle={t('library.lead')}
      actions={
        <>
          {addChannel}
          {addContent}
        </>
      }
    >
      {failure === null ? null : (
        <p className="help err" role="alert">
          {t(toErrorKey(failure))}
        </p>
      )}

      <CollectionsRail
        collections={allCollections}
        countIn={(collection) =>
          items.filter((item) => collection.itemIds.includes(item.id)).length
        }
        familyChildren={familyChildren}
        onCollectionsChanged={collections.reload}
        onChildrenChanged={children.reload}
      />

      <div className="lib-tools">
        <div className="search">
          <Icon name="search" small />
          <input
            className="input"
            type="search"
            value={query}
            placeholder={t('library.search')}
            aria-label={t('library.search')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {presentTypes.length < 2 ? null : (
          <Segmented<TypeFilter>
            label={t('library.typeFilter')}
            value={typeFilter}
            options={[
              { value: 'ALL', label: t('library.allTypes') },
              ...presentTypes.map((contentType) => ({
                value: contentType,
                label: t(`contentType.${contentType}`),
              })),
            ]}
            onChange={setTypeFilter}
          />
        )}
      </div>

      <div className="lib-scope">
        <div>
          <h2 className="t-h2">{t('library.allContent')}</h2>
          <p className="t-meta">
            {t('library.subtitle', {
              total: visible.length,
              approved: visible.filter((item) => item.approvedForChildIds.length > 0).length,
            })}
          </p>
        </div>
      </div>

      {content.resource.status === 'loading' && items.length === 0 ? (
        <p className="t-meta">{t('common.loading')}</p>
      ) : null}

      {content.resource.status === 'ready' || visible.length > 0 ? (
        <div className="items">
          {content.resource.status === 'ready' && visible.length === 0 ? emptyState() : null}
          {visible.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              familyChildren={familyChildren}
              collections={allCollections}
              felt={feltFor(item)}
              nativeLanguages={nativeLanguages}
              onChange={replaceItem}
              onRemoved={dropItem}
              onCollectionsChanged={collections.reload}
            />
          ))}
        </div>
      ) : null}
    </AppShell>
  );
};
